import React, { useCallback, useEffect } from 'react'
import {
  Typography,
  // ToggleButton,
  // ToggleButtonGroup,
  Grid,
  Box,
  useTheme,
  IconButton,
} from '@mui/material'
import {
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts'
import useCustomStyles from '../../../../../utils/hooks/CustomStylesHook'
import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import dayjs from 'dayjs'
import {
  GRAPH_PERIODS,
  DATE_DIRECTIONS,
  VITAL_KEYS,
  BLOOD_PRESSURE_TYPES,
} from '../Constant'
import { getVitalTracking } from '../../../../../services/DeviceServices'
import { useAppSelector } from '../../../../../app/hooks'
import { selectPatientData } from '../../../OpdPatientSlice'

const styles = (theme: any) => ({
  header: {
    textAlign: 'center',
    marginBottom: theme.spacing(3),
  },
  toggleGroup: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },

  toggleBtn: {
    fontSize: '14px',
    fontWeight: 600,
    minWidth: '60px',
  },
  dateDisplay: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
  },
  optimalBadge: {
    backgroundColor: '#0f0',
    color: '#000',
    padding: '4px 8px',
    borderRadius: 8,
    fontWeight: 'bold',
    textAlign: 'center',
  },
})

const BloodPressureTracking = ({
  selectedVital,
}: {
  selectedVital: string
}) => {
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)
  const [period] = React.useState(GRAPH_PERIODS.DAY)
  const [currentDate, setCurrentDate] = React.useState(dayjs())
  const [chartData, setChartData] = React.useState([])
  const [avgData, setAvgData] = React.useState<any>({})
  const patient = useAppSelector(selectPatientData)

  // const handlePeriodChange = (
  //   event: React.MouseEvent<HTMLElement>,
  //   newPeriod: string | null
  // ) => {
  //   if (newPeriod !== null) {
  //     setPeriod(newPeriod)
  //   }
  // }

  const changeDate = (direction: string) => {
    const amount =
      period === GRAPH_PERIODS?.DAY
        ? 1
        : period === GRAPH_PERIODS?.WEEK
        ? 7
        : 30
    setCurrentDate((prev) =>
      prev.add(direction === DATE_DIRECTIONS?.NEXT ? amount : -amount, 'day')
    )
  }

  const handleGetBloodGlucoseTracking = useCallback(async () => {
    const response = await getVitalTracking(
      patient?.muhId,
      currentDate.format('YYYY-MM-DD'),
      VITAL_KEYS?.BLOOD_PRESSURE
    )

    if (response?.success) {
      const graphPoints = response?.data?.bloodPressure?.map((item: any) => ({
        time: item?.time,
        value:
          selectedVital === BLOOD_PRESSURE_TYPES?.SYSTOLIC
            ? item?.svalue
            : item?.dvalue,
      }))

      setChartData(graphPoints || [])
      setAvgData({
        minimum:
          selectedVital === BLOOD_PRESSURE_TYPES?.SYSTOLIC
            ? response?.data?.sminBloodPressure?.toFixed(0)
            : response?.data?.dminBloodPressure?.toFixed(0),
        maximum:
          selectedVital === BLOOD_PRESSURE_TYPES?.SYSTOLIC
            ? response?.data?.smaxBloodPressure?.toFixed(0)
            : response?.data?.dmaxBloodPressure?.toFixed(0),
      })
    }
  }, [patient?.muhId, currentDate, selectedVital])

  useEffect(() => {
    handleGetBloodGlucoseTracking()
  }, [handleGetBloodGlucoseTracking])

  return (
    <Box m={3}>
      <Box className={classes?.toggleGroup}>
        {/* <ToggleButtonGroup
          value={period}
          exclusive
          onChange={handlePeriodChange}
          color='primary'
          size='small'
        >
          <ToggleButton value='day' className={classes?.toggleBtn}>
            D
          </ToggleButton>
          <ToggleButton value='week' className={classes?.toggleBtn}>
            W
          </ToggleButton>
          <ToggleButton value='month' className={classes?.toggleBtn}>
            M
          </ToggleButton>
        </ToggleButtonGroup> */}
        <Box className={classes?.dateDisplay}>
          <IconButton
            onClick={() => changeDate(DATE_DIRECTIONS?.PREVIOUS)}
            color='inherit'
            aria-label='previous date'
          >
            <ChevronLeft />
          </IconButton>
          <Typography>
            {currentDate.format(
              period === GRAPH_PERIODS?.DAY
                ? 'MMM DD, YYYY'
                : period === GRAPH_PERIODS?.WEEK
                ? '[Week of] MMM DD, YYYY'
                : 'MMM YYYY'
            )}
          </Typography>{' '}
          <IconButton
            onClick={() => changeDate(DATE_DIRECTIONS?.NEXT)}
            color='inherit'
            disabled={
              currentDate.isSame(dayjs(), 'day') ||
              currentDate.isAfter(dayjs(), 'day')
            }
            aria-label='next date'
          >
            <ChevronRight />
          </IconButton>
        </Box>
      </Box>

      <Grid container spacing={2} direction={'column'} alignSelf={'center'}>
        <Typography fontSize={'16px'} ml={4} mb={0.5}>
          mmHg
        </Typography>
        {/* <Typography variant='h6'>SpO2</Typography> */}
        {/* <Typography variant='subtitle1' alignSelf={'center'}>
          06:00
        </Typography>
        <Typography variant='h4'>97%</Typography> */}
        <ResponsiveContainer width='100%' height={170}>
          <AreaChart data={chartData}>
            <XAxis dataKey='time' fontSize={'12px'} />
            <YAxis domain={['dataMin', 'dataMax']} fontSize='12px' />
            <Tooltip />
            <Area
              dataKey='value'
              stroke='#5d4037'
              strokeWidth={2}
              fill='#d7ccc8'
              dot={true}
            />
          </AreaChart>
        </ResponsiveContainer>
        <Box
          display={'flex'}
          justifyContent={'center'}
          alignItems={'center'}
          gap={3}
          mt={1.5}
        >
          <Grid
            item
            xs={6}
            textAlign={'center'}
            border={'1px solid grey'}
            borderRadius={'20px'}
            p={2}
          >
            <Typography variant='h6' data-testid={'bpMinimum'}>
              {avgData?.minimum}
            </Typography>
            <Typography variant='subtitle2'>Minimum</Typography>
            {/* <Box className={classes?.optimalBadge}>Optimal</Box> */}
          </Grid>
          <Grid
            item
            xs={6}
            textAlign={'center'}
            border={'1px solid grey'}
            borderRadius={'20px'}
            p={2}
          >
            <Typography variant='h6' data-testid={'bpMaximum'}>
              {avgData?.maximum}
            </Typography>
            <Typography variant='subtitle2'>Maximum</Typography>
          </Grid>
        </Box>
      </Grid>
    </Box>
  )
}

export default BloodPressureTracking
